import apiClient from './apiClient';

export interface BudgetLine {
  id: string;
  wbsId?: number;
  wbsCode?: string;
  wbsName?: string;
  category: string;
  budget: number;
  committed: number;
  spent: number;
  available: number;
  availablePercent: number;
  status: 'OK' | 'WARNING' | 'OVERRUN';
}

export interface BudgetSummary {
  totalBudget: number;
  totalSpent: number;
  totalRemaining: number;
  utilizationPercent: number;
  budgetCount: number;
  overBudgetCount: number;
  utilizationStatus: 'OK' | 'WARNING' | 'OVERRUN';
}

export interface PhaseUtilization {
  phaseId: string;
  phaseName: string;
  totalBudget: number;
  totalSpent: number;
  totalRemaining: number;
  utilizationPercent: number;
  utilizationStatus: 'OK' | 'WARNING' | 'OVERRUN';
}

export interface BudgetOverrun {
  budgetId: string;
  wbsId?: number;
  wbsCode?: string;
  wbsName?: string;
  totalBudget: number;
  totalSpent: number;
  overrunAmount: number;
  overrunPercent: number;
  utilizationStatus: 'OVERRUN';
}

export interface BudgetFilters {
  projectId?: string;
  phaseId?: string;
  wbsId?: string;
  category?: string;
  status?: 'OK' | 'WARNING' | 'OVERRUN';
}

class BudgetService {
  /**
   * Get budget summary
   */
  async getSummary(projectId: string): Promise<BudgetSummary> {
    const response = await apiClient.get('/budgets/dashboard/summary', {
      params: { projectId },
    });
    return response.data;
  }

  /**
   * Get all budget lines with filters
   */
  async getBudgetLines(filters?: BudgetFilters): Promise<BudgetLine[]> {
    const response = await apiClient.get('/budgets', {
      params: {
        ...filters,
        utilizationStatus: filters?.status,
        status: undefined,
      },
    });
    return response.data.map((budget: any) => {
      const totalBudget = Number(budget.amount || 0);
      const totalSpent = Number(budget.spentToDate || 0);
      const utilizationPercent = Number(budget.utilizationPercent || 0);

      return {
        id: budget.id,
        wbsId: budget.wbsId,
        wbsCode: budget.wbsCode,
        wbsName: budget.wbsName,
        category: budget.category,
        budget: totalBudget,
        committed: 0,
        spent: totalSpent,
        available: Number(budget.totalRemaining ?? totalBudget - totalSpent),
        availablePercent: Math.max(0, 100 - utilizationPercent),
        status: budget.utilizationStatus,
      };
    });
  }

  /**
   * Get budget utilization by phase
   */
  async getPhaseUtilization(projectId: string): Promise<PhaseUtilization[]> {
    const response = await apiClient.get('/budgets/dashboard/phase-utilization', {
      params: { projectId },
    });
    return response.data;
  }

  /**
   * Get top budget overruns
   */
  async getTopOverruns(projectId: string, limit: number = 5): Promise<BudgetOverrun[]> {
    const response = await apiClient.get('/budgets/dashboard/overruns', {
      params: { projectId, limit },
    });
    return response.data;
  }

  /**
   * Create a budget line
   */
  async createBudgetLine(data: Partial<BudgetLine>): Promise<BudgetLine> {
    const response = await apiClient.post('/budgets', data);
    return response.data;
  }

  /**
   * Delete a budget line
   */
  async deleteBudgetLine(id: string): Promise<void> {
    await apiClient.delete(`/budgets/${id}`);
  }
}

export default new BudgetService();
